<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <span class="text-h4">Customer Anonymization</span>
            <v-spacer></v-spacer>
            <v-chip v-if="isAdmin" color="success" outlined>
              <v-icon left>mdi-shield-check</v-icon>
              Authorized
            </v-chip>
          </v-card-title>
          
          <v-card-text>
            <v-alert type="warning" outlined dense class="mb-4">
              <strong>Warning:</strong> Customer anonymization is a permanent action that cannot be undone. 
              This process will anonymize all personal data for compliance with GDPR and privacy regulations.
            </v-alert>

            <!-- Search/Request Section -->
            <v-row>
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title>Request Anonymization</v-card-title>
                  <v-card-text>
                    <v-form ref="anonymizeForm" v-model="formValid" @submit.prevent="submitAnonymization">
                      <v-text-field
                        v-model="customerId"
                        :rules="[rules.required, rules.numeric]"
                        label="Customer ID"
                        placeholder="Enter customer ID"
                        outlined
                        dense
                        type="number"
                      ></v-text-field>

                      <v-select
                        v-model="reason"
                        :items="reasonOptions"
                        :rules="[rules.required]"
                        label="Reason for Anonymization"
                        outlined
                        dense
                      ></v-select>

                      <v-textarea
                        v-model="additionalNotes"
                        label="Additional Notes (Optional)"
                        outlined
                        dense
                        rows="3"
                        placeholder="Enter any additional context or reference numbers"
                      ></v-textarea>

                      <v-checkbox
                        v-model="confirmAction"
                        :rules="[rules.required]"
                        color="error"
                      >
                        <template v-slot:label>
                          <span class="red--text">
                            I understand this action is permanent and cannot be reversed
                          </span>
                        </template>
                      </v-checkbox>

                      <v-btn
                        :disabled="!formValid || !confirmAction || processing"
                        :loading="processing"
                        color="error"
                        @click="submitAnonymization"
                      >
                        <v-icon left>mdi-account-remove</v-icon>
                        Anonymize Customer
                      </v-btn>
                    </v-form>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Status Check Section -->
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title>Check Anonymization Status</v-card-title>
                  <v-card-text>
                    <v-text-field
                      v-model="statusCheckId"
                      label="Customer ID"
                      placeholder="Enter customer ID to check status"
                      outlined
                      dense
                      type="number"
                      append-icon="mdi-magnify"
                      @click:append="checkStatus"
                      @keyup.enter="checkStatus"
                    ></v-text-field>

                    <v-alert
                      v-if="statusResult"
                      :type="statusAlertType"
                      outlined
                      dense
                      class="mt-2"
                    >
                      <div><strong>Status:</strong> {{ statusResult.status }}</div>
                      <div v-if="statusResult.message">{{ statusResult.message }}</div>
                    </v-alert>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Recent Requests Table -->
            <v-card outlined class="mt-4">
              <v-card-title>
                Recent Anonymization Requests
                <v-spacer></v-spacer>
                <v-btn icon @click="fetchRecentRequests" :loading="loadingHistory">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </v-card-title>
              <v-data-table
                :headers="tableHeaders"
                :items="recentRequests"
                :loading="loadingHistory"
                :items-per-page="10"
                class="elevation-0"
              >
                <template v-slot:item.status="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    small
                    outlined
                  >
                    {{ item.status }}
                  </v-chip>
                </template>
                <template v-slot:item.requested_at="{ item }">
                  {{ formatDate(item.requested_at) }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click="viewDetails(item)"
                      >
                        <v-icon small>mdi-information-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>View Details</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-card>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Success/Error Dialog -->
    <v-dialog v-model="resultDialog" max-width="500">
      <v-card>
        <v-card-title>
          <span :class="resultSuccess ? 'green--text' : 'red--text'">
            {{ resultSuccess ? 'Success' : 'Error' }}
          </span>
        </v-card-title>
        <v-card-text>
          {{ resultMessage }}
          <div v-if="jobDetails" class="mt-2">
            <div><strong>Job ID:</strong> {{ jobDetails.job_id }}</div>
            <div><strong>Job Name:</strong> {{ jobDetails.job_name }}</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="resultDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import customerAnonymizationApi from '@/services/api/customerAnonymizationApi'

export default {
  name: 'CustomerAnonymizationIndex',
  
  data() {
    return {
      // Form data
      customerId: '',
      reason: '',
      additionalNotes: '',
      confirmAction: false,
      formValid: false,
      processing: false,
      
      // Status check
      statusCheckId: '',
      statusResult: null,
      
      // Recent requests
      recentRequests: [],
      loadingHistory: false,
      
      // Result dialog
      resultDialog: false,
      resultSuccess: false,
      resultMessage: '',
      jobDetails: null,
      
      // Options
      reasonOptions: [
        { text: 'GDPR Request', value: 'GDPR_REQUEST' },
        { text: 'CCPA Request', value: 'CCPA_REQUEST' },
        { text: 'Customer Request', value: 'CUSTOMER_REQUEST' },
        { text: 'Data Retention Policy', value: 'DATA_RETENTION' },
        { text: 'Legal Requirement', value: 'LEGAL_REQUIREMENT' },
        { text: 'Other', value: 'OTHER' }
      ],
      
      // Table configuration
      tableHeaders: [
        { text: 'Customer ID', value: 'customer_id' },
        { text: 'Status', value: 'status' },
        { text: 'Reason', value: 'reason' },
        { text: 'Requested By', value: 'requested_by' },
        { text: 'Requested At', value: 'requested_at' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      
      // Validation rules
      rules: {
        required: value => !!value || 'Required',
        numeric: value => !isNaN(value) || 'Must be a number'
      }
    }
  },
  
  computed: {
    ...mapGetters(['currentUser', 'isAdmin', 'isAccountAdmin']),
    
    statusAlertType() {
      if (!this.statusResult) return 'info'
      const status = this.statusResult.status ? this.statusResult.status.toUpperCase() : ''
      if (status === 'SUCCESS' || status === 'ANONYMIZED') return 'success'
      if (status === 'FAILED' || status === 'ERROR') return 'error'
      if (status === 'PENDING' || status === 'PROCESSING') return 'info'
      return 'warning'
    }
  },
  
  mounted() {
    this.fetchRecentRequests()
  },
  
  methods: {
    async submitAnonymization() {
      if (!this.$refs.anonymizeForm.validate()) return
      
      this.processing = true
      try {
        const payload = {
          anonymization: {
            reason: this.reason
          }
        }
        
        if (this.additionalNotes) {
          payload.anonymization.notes = this.additionalNotes
        }
        
        const response = await customerAnonymizationApi.create(this.customerId, payload)
        
        this.resultSuccess = true
        this.resultMessage = response.data.message || 'Anonymization request submitted successfully'
        this.jobDetails = response.data
        this.resultDialog = true
        
        // Reset form
        this.$refs.anonymizeForm.reset()
        this.confirmAction = false
        
        // Refresh history
        this.fetchRecentRequests()
        
      } catch (error) {
        this.resultSuccess = false
        this.resultMessage = (error.response && error.response.data && error.response.data.error) || 'Failed to submit anonymization request'
        this.jobDetails = null
        this.resultDialog = true
      } finally {
        this.processing = false
      }
    },
    
    async checkStatus() {
      if (!this.statusCheckId) return
      
      try {
        const response = await customerAnonymizationApi.getStatus(this.statusCheckId)
        this.statusResult = response.data
      } catch (error) {
        this.statusResult = {
          status: 'ERROR',
          message: (error.response && error.response.data && error.response.data.error) || 'Failed to check status'
        }
      }
    },
    
    async fetchRecentRequests() {
      this.loadingHistory = true
      try {
        // TODO: Implement when history endpoint is available
        // const response = await customerAnonymizationApi.getHistory()
        // this.recentRequests = response.data.history
        
        // Mock data for now
        this.recentRequests = []
      } catch (error) {
        console.error('Failed to fetch history:', error)
      } finally {
        this.loadingHistory = false
      }
    },
    
    viewDetails(item) {
      // TODO: Implement detailed view dialog or navigation
      console.log('View details for:', item)
    },
    
    getStatusColor(status) {
      const statusMap = {
        'SUCCESS': 'success',
        'ANONYMIZED': 'success',
        'FAILED': 'error',
        'ERROR': 'error',
        'PENDING': 'warning',
        'PROCESSING': 'info',
        'ALREADY_ANONYMIZED': 'grey'
      }
      return statusMap[status ? status.toUpperCase() : ''] || 'grey'
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      return new Date(dateString).toLocaleString()
    }
  }
}
</script>

<style scoped>
.disabled-user {
  opacity: 0.6;
  text-decoration: line-through;
}
</style>